import { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { motion } from 'framer-motion';
// import EmailIcon from '@mui/icons-material/Email';
// import LocationOnIcon from '@mui/icons-material/LocationOn';
// import PhoneIcon from '@mui/icons-material/Phone';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// const contactInfo = [
//   {
//     icon: <EmailIcon sx={{ fontSize: 22 }} />,
//     label: 'Email',
//     value: 'hello@getyoursitetoday.com',
//     color: '#7C3AED',
//   },
//   {
//     icon: <LocationOnIcon sx={{ fontSize: 22 }} />,
//     label: 'Location',
//     value: 'London, United Kingdom',
//     color: '#F59E0B',
//   },
//   {
//     icon: <PhoneIcon sx={{ fontSize: 22 }} />,
//     label: 'Phone',
//     value: '+44 7700 900 123',
//     color: '#06B6D4',
//   },
// ];

const projectTypes = [
  'Landing Page',
  'E-Commerce Site',
  'Booking System',
  'Business Dashboard',
  'Contact Form App',
  'SEO-Optimized Site',
  'Other / Unsure',
];

const makeWebhookUrl = process.env.REACT_APP_MAKE_WEBHOOK_URL;

export default function Contact() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    projectType: '',
    message: '',
  });
  const [alertState, setAlertState] = useState({
    open: false,
    severity: 'success',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = 'Name is required';
    if (!form.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!form.projectType)
      newErrors.projectType = 'Please select a project type';
    if (!form.message.trim()) newErrors.message = 'Message is required';
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!makeWebhookUrl) {
      setAlertState({
        open: true,
        severity: 'error',
        message:
          'Form is not configured. Please set REACT_APP_MAKE_WEBHOOK_URL.',
      });
      return;
    }

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      projectType: form.projectType,
      message: form.message.trim(),
      submittedAt: new Date().toISOString(),
      source: 'portfolio-contact-form',
    };

    setIsSubmitting(true);

    try {
      const response = await fetch(makeWebhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(
          `Webhook request failed with status ${response.status}`,
        );
      }

      setAlertState({
        open: true,
        severity: 'success',
        message: "Message sent! I'll be in touch within 24 hours.",
      });
      setForm({ name: '', email: '', projectType: '', message: '' });
      setErrors({});
    } catch {
      setAlertState({
        open: true,
        severity: 'error',
        message: 'Message could not be sent right now. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAlertClose = (_event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setAlertState((prev) => ({ ...prev, open: false }));
  };

  return (
    <Box
      id='contact'
      sx={{
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(180deg, #12101E 0%, #0F0F1A 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background accents */}
      <Box
        sx={{
          position: 'absolute',
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: '#7C3AED',
          filter: 'blur(160px)',
          opacity: 0.07,
          top: '10%',
          left: '40%',
          transform: 'translateX(-50%)',
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth='lg'>
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
        >
          <Box sx={{ textAlign: 'center', mb: 7 }}>
            <Box
              sx={{
                display: 'inline-block',
                px: 2,
                py: 0.5,
                borderRadius: 50,
                background: 'rgba(124,58,237,0.12)',
                border: '1px solid rgba(124,58,237,0.3)',
                mb: 2,
              }}
            >
              <Typography
                variant='overline'
                sx={{ color: '#A78BFA', fontWeight: 700, letterSpacing: 2 }}
              >
                Contact
              </Typography>
            </Box>
            <Typography
              variant='h2'
              sx={{
                fontSize: { xs: '2rem', md: '3rem' },
                fontWeight: 800,
                mb: 2,
              }}
            >
              Let&apos;s Work{' '}
              <Box component='span' className='gradient-text'>
                Together
              </Box>
            </Typography>
            <Typography
              variant='body1'
              sx={{
                color: 'rgba(255,255,255,0.55)',
                maxWidth: 500,
                mx: 'auto',
                fontSize: '1.05rem',
              }}
            >
              Ready to transform your online presence? Drop me a message and
              I&apos;ll get back to you within 24 hours.
            </Typography>
          </Box>
        </motion.div>

        <Grid container spacing={4} alignItems='flex-start'>
          {/* Contact info sidebar */}
          <Grid size={{ xs: 12, md: 4 }}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7 }}
            >
              <Box
                sx={{
                  p: 4,
                  borderRadius: '20px',
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  backdropFilter: 'blur(12px)',
                  mb: { xs: 2, md: 0 },
                }}
              >
                <Typography
                  variant='h6'
                  sx={{ fontFamily: 'Poppins', fontWeight: 700, mb: 1 }}
                >
                  Get in Touch
                </Typography>
                <Typography
                  variant='body2'
                  sx={{
                    color: 'rgba(255,255,255,0.55)',
                    mb: 4,
                    lineHeight: 1.7,
                  }}
                >
                  Have a project in mind? I&apos;d love to hear about it. Fill
                  out the form and I&apos;ll be in touch
                </Typography>

                {/* <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {contactInfo.map((info) => (
                    <Box key={info.label} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: '12px',
                          background: `${info.color}15`,
                          border: `1px solid ${info.color}30`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: info.color,
                          flexShrink: 0,
                        }}
                      >
                        {info.icon}
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', mb: 0.25, fontWeight: 600 }}>
                          {info.label}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>
                          {info.value}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box> */}

                {/* Response time badge */}
                <Box
                  sx={{
                    mt: 4,
                    p: 2,
                    borderRadius: '12px',
                    background: 'rgba(16,185,129,0.1)',
                    border: '1px solid rgba(16,185,129,0.25)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      background: '#10B981',
                      boxShadow: '0 0 8px #10B981',
                      animation: 'pulse-glow 2s ease-in-out infinite',
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant='body2'
                    sx={{
                      color: '#34D399',
                      fontWeight: 600,
                      fontSize: '0.85rem',
                    }}
                  >
                    Usually responds within 24 hours
                  </Typography>
                </Box>
              </Box>
            </motion.div>
          </Grid>

          {/* Contact form */}
          <Grid size={{ xs: 12, md: 8 }}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7 }}
            >
              <Box
                component='form'
                onSubmit={handleSubmit}
                sx={{
                  p: { xs: 3, md: 5 },
                  borderRadius: '20px',
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  backdropFilter: 'blur(12px)',
                }}
              >
                <Grid container spacing={3}>
                  {/* Name */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label='Your Name'
                      name='name'
                      value={form.name}
                      onChange={handleChange}
                      error={!!errors.name}
                      helperText={errors.name}
                      variant='outlined'
                      InputLabelProps={{
                        style: { color: 'rgba(255,255,255,0.5)' },
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: '#F1F5F9',
                          '& fieldset': {
                            borderColor: 'rgba(255,255,255,0.12)',
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(124,58,237,0.5)',
                          },
                          '&.Mui-focused fieldset': { borderColor: '#7C3AED' },
                        },
                      }}
                    />
                  </Grid>

                  {/* Email */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label='Email Address'
                      name='email'
                      type='email'
                      value={form.email}
                      onChange={handleChange}
                      error={!!errors.email}
                      helperText={errors.email}
                      variant='outlined'
                      InputLabelProps={{
                        style: { color: 'rgba(255,255,255,0.5)' },
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: '#F1F5F9',
                          '& fieldset': {
                            borderColor: 'rgba(255,255,255,0.12)',
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(124,58,237,0.5)',
                          },
                          '&.Mui-focused fieldset': { borderColor: '#7C3AED' },
                        },
                      }}
                    />
                  </Grid>

                  {/* Project type */}
                  <Grid size={{ xs: 12 }}>
                    <FormControl fullWidth error={!!errors.projectType}>
                      <InputLabel sx={{ color: 'rgba(255,255,255,0.5)' }}>
                        Project Type
                      </InputLabel>
                      <Select
                        name='projectType'
                        value={form.projectType}
                        onChange={handleChange}
                        label='Project Type'
                        sx={{
                          color: '#F1F5F9',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255,255,255,0.12)',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(124,58,237,0.5)',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#7C3AED',
                          },
                          '& .MuiSvgIcon-root': {
                            color: 'rgba(255,255,255,0.5)',
                          },
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              background: '#1a1a2e',
                              border: '1px solid rgba(255,255,255,0.1)',
                              '& .MuiMenuItem-root': {
                                color: '#F1F5F9',
                                '&:hover': {
                                  background: 'rgba(124,58,237,0.15)',
                                },
                                '&.Mui-selected': {
                                  background: 'rgba(124,58,237,0.25)',
                                },
                              },
                            },
                          },
                        }}
                      >
                        {projectTypes.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.projectType && (
                        <Typography
                          variant='caption'
                          sx={{ color: '#f44336', mt: 0.5, ml: 1.75 }}
                        >
                          {errors.projectType}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  {/* Message */}
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label='Tell me about your project...'
                      name='message'
                      value={form.message}
                      onChange={handleChange}
                      error={!!errors.message}
                      helperText={errors.message}
                      multiline
                      rows={5}
                      variant='outlined'
                      InputLabelProps={{
                        style: { color: 'rgba(255,255,255,0.5)' },
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: '#F1F5F9',
                          '& fieldset': {
                            borderColor: 'rgba(255,255,255,0.12)',
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(124,58,237,0.5)',
                          },
                          '&.Mui-focused fieldset': { borderColor: '#7C3AED' },
                        },
                      }}
                    />
                  </Grid>

                  {/* Submit */}
                  <Grid size={{ xs: 12 }}>
                    <Button
                      type='submit'
                      variant='contained'
                      size='large'
                      fullWidth
                      disabled={isSubmitting}
                      endIcon={isSubmitting ? null : <SendIcon />}
                      sx={{
                        py: 1.75,
                        fontSize: '1rem',
                        fontWeight: 700,
                        background:
                          'linear-gradient(135deg, #7C3AED 0%, #F59E0B 100%)',
                        backgroundSize: '200% 200%',
                        color: '#fff',
                        boxShadow: '0 8px 30px rgba(124,58,237,0.35)',
                        transition: 'all 0.4s ease',
                        '&:hover': {
                          backgroundPosition: 'right center',
                          boxShadow: '0 12px 40px rgba(124,58,237,0.55)',
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      {/* Success snackbar */}
      <Snackbar
        open={alertState.open}
        autoHideDuration={5000}
        onClose={handleAlertClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          icon={
            alertState.severity === 'success' ? <CheckCircleIcon /> : undefined
          }
          severity={alertState.severity}
          onClose={handleAlertClose}
          sx={{
            background:
              alertState.severity === 'success'
                ? 'rgba(16,185,129,0.15)'
                : 'rgba(239,68,68,0.15)',
            border:
              alertState.severity === 'success'
                ? '1px solid rgba(16,185,129,0.4)'
                : '1px solid rgba(239,68,68,0.45)',
            color: alertState.severity === 'success' ? '#34D399' : '#FCA5A5',
            backdropFilter: 'blur(10px)',
            '& .MuiAlert-icon': {
              color: alertState.severity === 'success' ? '#34D399' : '#F87171',
            },
          }}
        >
          {alertState.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
